import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import '@awesome.me/webawesome/dist/components/button/button.js';

import '../ns-icon/ns-icon.js';
import '../ns-elements/ns-urgent-marker.js';
import type Notification from '../models/notification.js';
import { renderDate } from '../helpers/format-render.js';
import {
  NOTIFICATION_CATEGORY_LABELS,
  notificationActions,
  notificationPath,
  priorityMarker,
} from '../helpers/notifications.js';
import type { NotificationAction } from '../helpers/notifications.js';
import { claimJob } from '../helpers/job-claim.js';
import { ltnAuthServiceContext, ltnToastServiceContext } from '../ns-services/contexts.js';
import type { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';
import type { LtnToastService } from '../ltn-toast/ltn-toast-service.js';

const ACTION_RUNNERS: Record<NotificationAction['id'], (token: string, notification: Notification) => Promise<void>> = {
  CLAIM_JOB: (token, notification) => claimJob(token, notification.entity.id),
};

/**
 * One notification as the flyout and the notifications page list it. Opening one with a page, or ticking one without,
 * fires `notification-open` or `notification-read`; the list marks it read. An action that succeeds opens it too.
 */
@customElement('ns-notification-row')
export class NSNotificationRow extends LitElement {
  @property({ attribute: false })
  notification?: Notification;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @state() private __acting = false;

  static styles = css`
    :host {
      display: block;
    }

    .row {
      display: flex;
      align-items: center;
      gap: var(--wa-space-xs);
      padding: var(--wa-space-s);
      border-radius: var(--ns-card-border-radius, 0.5em);
      color: var(--wa-color-text-normal);
      background: var(--wa-color-surface-default);
      transition: background-color 0.15s ease-out;
    }

    .row[data-openable]:hover {
      background: var(--wa-color-neutral-fill-quiet);
    }

    .row[data-read] {
      opacity: 0.6;
    }

    .body {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--wa-space-3xs);
      min-width: 0;
    }

    button.body {
      border: none;
      background: transparent;
      font: inherit;
      text-align: left;
      color: inherit;
      padding: 0;
      cursor: pointer;
      width: 100%;
    }

    .title {
      display: flex;
      align-items: center;
      gap: var(--wa-space-2xs);
      font-weight: 600;
      font-size: var(--wa-font-size-s);
      line-height: 1.3;
    }

    .description {
      font-size: var(--wa-font-size-xs);
      opacity: 0.8;
      line-height: 1.3;
    }

    .meta {
      font-size: var(--wa-font-size-xs);
      opacity: 0.6;
      margin-top: var(--wa-space-3xs);
    }

    .chevron {
      flex-shrink: 0;
      opacity: 0.5;
      font-size: var(--wa-font-size-m);
    }

    .dismiss {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border: none;
      border-radius: var(--wa-border-radius-pill);
      background: transparent;
      padding: 0;
      cursor: pointer;
      opacity: 0.6;
      transition:
        opacity 0.15s ease-out,
        background-color 0.15s ease-out;

      --icon-height: 14px;
      --icon-width: 14px;
    }

    .dismiss:hover {
      opacity: 1;
      background: var(--wa-color-neutral-fill-normal);
    }

    wa-button {
      flex-shrink: 0;
    }
  `;

  private __fire(name: 'notification-open' | 'notification-read') {
    this.dispatchEvent(
      new CustomEvent(name, { detail: { notification: this.notification }, bubbles: true, composed: true }),
    );
  }

  private async __act(action: NotificationAction) {
    const notification = this.notification;
    if (!notification || !this.__auth?.token || this.__acting) return;

    this.__acting = true;
    try {
      await ACTION_RUNNERS[action.id](this.__auth.token, notification);
      this.__fire('notification-open');
    } catch (err) {
      this.__toast?.queueToast({
        label: err instanceof Error ? err.message : 'Unable to do that.',
        variant: 'danger',
        timeout: 6000,
      });
    } finally {
      this.__acting = false;
    }
  }

  render() {
    const notification = this.notification;
    if (!notification) return nothing;

    const openable = !!notificationPath(notification);
    const read = !!notification.readAt;
    const marker = priorityMarker(notification.priority);
    const category = notification.category ? NOTIFICATION_CATEGORY_LABELS[notification.category] : null;
    const body = html`
      <span class="title">
        ${notification.title}
        ${marker
          ? html`
              <ns-urgent-marker label=${marker}></ns-urgent-marker>
            `
          : nothing}
      </span>
      ${notification.description
        ? html`
            <span class="description">${notification.description}</span>
          `
        : nothing}
      <span class="meta">
        ${renderDate(notification.createdAt, 'dateTime')}${category ? ` · ${category}` : nothing}
      </span>
    `;
    const actions = notificationActions(notification).map(
      (action) => html`
        <wa-button size="small" ?loading=${this.__acting} @click=${() => this.__act(action)}>${action.label}</wa-button>
      `,
    );

    return html`
      <div class="row" ?data-openable=${openable} ?data-read=${read}>
        ${openable
          ? html`
              <button type="button" class="body" @click=${() => this.__fire('notification-open')}>${body}</button>
              ${actions}
              <span class="chevron" aria-hidden="true">›</span>
            `
          : html`
              <div class="body">${body}</div>
              ${actions}
              ${read
                ? nothing
                : html`
                    <button
                      type="button"
                      class="dismiss"
                      aria-label="Mark as read"
                      title="Mark as read"
                      @click=${() => this.__fire('notification-read')}
                    >
                      <ns-icon icon="tick" aria-hidden="true"></ns-icon>
                    </button>
                  `}
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-notification-row': NSNotificationRow;
  }
}
